<template>
  <div class="row sba-open-frame">
    <div class="col s12 sba-open-frame-col">
      <LoadingDiv v-if="!isReady" />

      <div class="compliance-invoices sba-open-workbench" v-else>
        <div id="compliance-invoices-top"></div>

        <div class="invoice-review-head sba-open-head">
          <div class="sba-open-title">
            <label class="active teal-text">Compliance Invoices</label>
            <h5>Invoices Review</h5>
            <p>Search, inspect, and open invoice records that require compliance visibility.</p>
          </div>

          <div class="invoice-head-filter" v-show="showSearch">
            <FilterInvoice compact-client-search @search="handleSearch" />
          </div>

          <span class="invoice-count sba-open-count" v-show="!showSearch">
            {{ selectedInvoiceCount }}
            <small>{{ selectedInvoiceCount === 1 ? "invoice" : "invoices" }}</small>
          </span>
        </div>

        <div class="progress invoice-progress" v-if="isPageLoading">
          <div class="indeterminate"></div>
        </div>

        <!-- Invoices -->
        <div class="invoice-list-card">
          <!-- Start -->
          <div v-for="(invoice, index) in invoices" :key="invoice.id">
            <!-- Principal -->
            <div
              class="invoice-row-card sba-open-row"
              :class="{
                'is-pending': invoice.status == 'p',
                'is-complete': invoice.status == 'c',
                'is-grace': invoice.status == 'z',
                'is-deleted': invoice.status == 'd',
              }"
              v-if="currInvoice?.id === invoice?.id || !currInvoice"
            >
              <div class="row mb-zero">
                <div class="col s1">
                  <span class="invoice-number">{{ numberBadge(index) }}</span>
                </div>
                <div class="col s10">
                  <p class="invoice-status-line">
                    <span
                      v-if="
                        invoice.status == 'p' && invoice.start < currServerDate
                      "
                      >Delayed Invoice<i class="tiny material-icons"
                        >schedule</i
                      ></span
                    >
                    <span
                      v-if="
                        invoice.status == 'p' && invoice.start >= currServerDate
                      "
                      >Pending Invoice</span
                    >
                    <span
                      class="orange-text text-darken-4"
                      v-if="invoice.status == 'z'"
                      >Unpaid (Grace Period)</span
                    >
                    <span v-if="invoice.status == 'c'"
                      >Completed Payment<i class="tiny material-icons teal-text"
                        >check_circle</i
                      ></span
                    >
                    <span class="red-text" v-if="invoice.status == 'd'"
                      >Deleted Invoice</span
                    >
                  </p>
                  <h6 class="invoice-client">
                    {{ invoiceClientName(invoice) }}
                    <span class="invoice-category">{{
                      invoiceClientCat(invoice)
                    }}</span>
                  </h6>
                  <h6 class="invoice-amount">
                    ₦{{
                      changeToNaira(invoice.input_total, invoice.output_total)
                    }}
                    <span class="invoice-period">
                      <small>
                        {{
                          new Date(invoice.start).toLocaleString(undefined, {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })
                        }}
                      </small>

                      -

                      <small>
                        {{
                          new Date(invoice.end).toLocaleString(undefined, {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })
                        }}
                      </small>
                    </span>
                  </h6>
                </div>
                <div class="col s1">
                  <span class="right" v-if="!currInvoice">
                    <button
                      type="button"
                      class="invoice-action sba-open-action right"
                      :class="{
                        'is-late': invoice.start < currServerDate && (invoice.status == 'p' || invoice.status == 'z'),
                        'is-normal': !(
                          invoice.start < currServerDate && (invoice.status == 'p' || invoice.status == 'z')
                        ),
                      }"
                      @click="handleView(invoice)"
                      title="view details"
                    >
                      <i class="material-icons">chevron_right</i>
                      <span class="invoice-action-label">Details</span>
                    </button>
                  </span>
                  <span class="right" v-else>
                    <button type="button" class="invoice-action sba-open-action is-close right" @click="handleClear()" title="go back">
                      <i class="material-icons">keyboard_return</i>
                      <span class="invoice-action-label">Back</span>
                    </button>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Invoice details -->
        <InvoiceDiv :currInvoice="currInvoice" v-if="currInvoice" />

        <!-- Pages -->
        <template v-else>
          <div class="invoice-empty-state" v-if="!invoices.length">
            <div class="invoice-empty-icon">
              <i class="material-icons">receipt_long</i>
            </div>
            <h6>{{ emptyInvoiceTitle }}</h6>
            <p>{{ emptyInvoiceDescription }}</p>
          </div>

          <PaginationDiv :payLoad="allPages" @newPage="handlePage" v-else />
        </template>
      </div>
    </div>
  </div>
</template>
            
  <script>
import { computed, onMounted, ref, watch } from "vue";
import { changeToMoney } from "@/composables/fxLibrary/coreFx";
import getItems from "@/composables/getItems";
import InvoiceDiv from "@/components/invoice/InvoiceDiv.vue";
import FilterInvoice from "@/components/invoice/FilterInvoiceDiv.vue";
import PaginationDiv from "@/components/pager/PaginationDiv.vue";
import LoadingDiv from "@/components/LoadingDiv.vue";

export default {
  name: "ComplianceInvoicesComponent",
  components: { LoadingDiv, PaginationDiv, InvoiceDiv, FilterInvoice },
  props: ["resetKey"],
  setup(props) {
    const response = ref(null);
    const isReady = ref(false);
    const isLoading = ref(false);
    const isPageLoading = ref(false);
    const isSubmitting = ref(false);
    const error = ref(null);
    const currInvoice = ref(false);
    const currUrl = ref(null);
    const currHook = ref(null);
    const currServerDate = ref(null);
    const showSearch = ref(true);
    const searchItem = ref(null);
    const searchTitle = ref(null);
    const filterState = ref({
      category: "",
      categoryLabel: "Open Invoices",
      client: "",
      clientLabel: "",
    });

    // get function
    const getItemsFromApi = (table) => {
      (async () => {
        const { res, err } = await getItems(table);

        if (res.value) {
          response.value = res.value.data;
          currServerDate.value = res.value.server;
        } else {
          error.value = err.value;
        }

        // page is ready
        isReady.value = true;
        isLoading.value = false;
        isPageLoading.value = false;
      })();
    };

    // to naira
    const changeToNaira = (i, o) => {
      // sort value
      let val = i - o;
      // conclude
      const { fx } = changeToMoney(val);
      return fx.value;
    };

    const invoiceClient = (invoice) => {
      return invoice?.policy?.client || invoice?.client || {};
    };

    const invoiceClientName = (invoice) => {
      return invoiceClient(invoice)?.name || "Client";
    };

    const invoiceClientCat = (invoice) => {
      return invoiceClient(invoice)?.cat?.toUpperCase() || "";
    };

    const numberBadge = (num) => {
      return (
        (allPages.value.current_page - 1) * allPages.value.per_page + num + 1
      );
    };

    // starter
    onMounted(() => {
      // loader
      isReady.value = false;
      // start from the top
      getInvoices();
    });

    // invoices
    const getInvoices = () => {
      // set current Url
      currUrl.value = "group/invoices/group";
      // set current Hook
      currHook.value = "?";
      // get items from function
      getItemsFromApi(currUrl.value);
      // clear
      handleClear();
    };

    // handle search (emitted from component)
    const handleSearch = (val) => {
      // start loading
      isLoading.value = true;
      // set current Url
      currUrl.value = val.currUrl;
      // set current Hook
      currHook.value = val.currHook;
      // set search title
      searchTitle.value = val.searchTitle;
      // set current filter state
      filterState.value = val.filterState || filterState.value;
      // get items from function
      getItemsFromApi(currUrl.value);
      // clear
      handleClear();
    };

    // switch page
    const handlePage = (page) => {
      // loader
      isLoading.value = true;
      isPageLoading.value = true;
      scrollToInvoiceTop();
      // get new page items from function
      getItemsFromApi(`${currUrl.value}${currHook.value}page=${page}`);
      // clear
      handleClear();
    };

    const scrollToInvoiceTop = () => {
      requestAnimationFrame(() => {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      });
    };

    // enrollee ction
    const handleClear = () => {
      currInvoice.value = null;
      showSearch.value = true;
    };

    watch(
      () => props.resetKey,
      () => {
        handleClear();
      }
    );

    // enrollee ction
    const handleView = (val) => {
      currInvoice.value = val;
      showSearch.value = false;
      scrollToInvoiceTop();
    };

    // output results
    const invoices = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value.data;
      }
    });

    // output pages
    const allPages = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value; // get all
      }
    });

    const selectedInvoiceCount = computed(() => {
      return Number(currInvoice.value?.invoices_count || 0);
    });

    const emptyInvoiceTitle = computed(() => {
      const categoryLabel = filterState.value.categoryLabel || "Open Invoices";
      const clientLabel = filterState.value.clientLabel;

      if (filterState.value.category === "all" && !clientLabel) {
        return "No invoices found";
      }

      if (clientLabel) {
        return `No invoices found for ${clientLabel}`;
      }

      return `No ${categoryLabel.toLowerCase()} invoices found`;
    });

    const emptyInvoiceDescription = computed(() => {
      const categoryLabel = filterState.value.categoryLabel || "Open Invoices";
      const clientLabel = filterState.value.clientLabel;

      if (filterState.value.category === "all" && !clientLabel) {
        return "There are no invoice records matching the current compliance view.";
      }

      if (clientLabel && filterState.value.category !== "all") {
        return `There are no ${categoryLabel.toLowerCase()} invoice records for this client.`;
      }

      if (clientLabel) {
        return "There are no invoice records for this client.";
      }

      return `There are no invoice records under the ${categoryLabel} filter.`;
    });

    return {
      searchTitle,
      handleSearch,
      handlePage,
      handleClear,
      handleView,
      numberBadge,
      changeToNaira,
      invoiceClientName,
      invoiceClientCat,
      currServerDate,
      currInvoice,
      showSearch,
      searchItem,
      isReady,
      isLoading,
      isPageLoading,
      isSubmitting,
      error,
      invoices,
      allPages,
      selectedInvoiceCount,
      emptyInvoiceTitle,
      emptyInvoiceDescription,
    };
  },
};
</script>
            
    <style scoped>
.compliance-invoices {
  color: #263238;
  width: 100%;
}

.invoice-review-head,
.invoice-filter-card,
.invoice-list-card,
.invoice-row-card {
  background: #ffffff;
  border: 1px solid #eeeeee;
  border-radius: 6px;
  box-shadow: none;
}

.invoice-review-head {
  align-items: flex-end;
  border-color: #eeeeee;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  box-shadow: none;
  display: flex;
  gap: 18px;
  justify-content: space-between;
  margin-bottom: 0;
  padding: 20px 22px;
}

.invoice-review-head h5 {
  color: #263238;
  font-weight: 700;
  margin: 4px 0 0;
}

.invoice-review-head p {
  color: #607d8b;
  line-height: 1.55;
  margin: 8px 0 0;
  max-width: 760px;
}

.invoice-count {
  align-items: flex-end;
  background: #fafafa;
  border: 1px solid #eeeeee;
  border-radius: 6px;
  color: #263238;
  display: inline-flex;
  flex-direction: column;
  font-size: 1.18rem;
  font-weight: 700;
  line-height: 1.1;
  min-width: 86px;
  padding: 10px 12px;
}

.invoice-count small {
  color: #455a64;
  font-size: 0.75rem;
  font-weight: 600;
}

.invoice-head-filter {
  background: #fafafa;
  border: 1px solid #eeeeee;
  border-radius: 6px;
  box-shadow: none;
  flex: 0 1 650px;
  padding: 6px;
}

.invoice-head-filter :deep(.section) {
  margin: 0;
  padding: 0;
}

.invoice-head-filter :deep(.row) {
  align-items: stretch;
  display: flex;
  gap: 0;
  margin-bottom: 0;
}

.invoice-head-filter :deep(.col) {
  min-width: 0;
  padding-left: 0 !important;
  padding-right: 0 !important;
}

.invoice-head-filter :deep(.col + .col) {
  border-left: 1px solid #eeeeee;
}

.invoice-head-filter :deep(.input-field) {
  height: 48px;
  margin-bottom: 0;
  margin-top: 0;
}

.invoice-head-filter :deep(nav) {
  background: transparent !important;
  border: 0;
  box-shadow: none !important;
  height: 48px;
  line-height: 48px;
}

.invoice-head-filter :deep(.nav-wrapper) {
  background: transparent;
  height: 48px;
  line-height: 48px;
}

.invoice-head-filter :deep(input),
.invoice-head-filter :deep(.select-wrapper input.select-dropdown) {
  background: transparent;
  border: none !important;
  border-radius: 0;
  box-sizing: border-box;
  color: #263238 !important;
  font-size: 1.08rem;
  font-weight: 600;
  height: 48px !important;
  line-height: 48px;
  margin: 0;
  padding: 0 46px 0 18px !important;
  width: 100%;
}

.invoice-head-filter :deep(input:focus) {
  box-shadow: inset 0 -2px 0 #00897b !important;
}

.invoice-head-filter :deep(.material-icons) {
  color: #00897b !important;
  height: 48px;
  line-height: 48px;
  right: 10px;
  top: 0;
}

.invoice-head-filter :deep(label) {
  color: #607d8b !important;
}

.invoice-filter-card {
  background: #fafafa;
  padding: 16px;
}

.invoice-list-card {
  background: #ffffff;
  border: 1px solid #eeeeee;
  border-top: 0;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  display: grid;
  gap: 0;
  margin-top: 0;
}

.invoice-row-card {
  align-items: stretch;
  border: 0;
  border-radius: 0;
  border-top: 1px solid #eeeeee;
  box-shadow: none;
  padding: 16px 18px;
  position: relative;
  transition: background-color 0.18s ease, border-color 0.18s ease;
}

.invoice-row-card > .row {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 0;
}

.invoice-row-card:hover {
  background: #fafafa;
  border-color: #e0e0e0;
}

.invoice-list-card > div:first-child .invoice-row-card {
  border-top: 0;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  box-shadow: none;
}

.invoice-row-card.is-pending {
  border-left-color: #eeeeee;
}

.invoice-row-card.is-complete {
  border-left-color: #eeeeee;
}

.invoice-row-card.is-grace {
  border-left-color: #eeeeee;
}

.invoice-row-card.is-deleted {
  border-left-color: #eeeeee;
}

.invoice-number {
  align-items: center;
  background: transparent;
  border-radius: 0;
  color: #607d8b;
  display: inline-flex;
  font-size: 0.92rem;
  font-weight: 700;
  height: auto;
  justify-content: center;
  width: auto;
}

.invoice-status-line {
  align-items: center;
  background: #eceff1;
  border-radius: 999px;
  color: #607d8b;
  display: flex;
  font-size: 0.82rem;
  font-weight: 700;
  gap: 5px;
  justify-self: start;
  margin: 0;
  padding: 5px 10px;
  width: max-content;
}

.invoice-status-line i {
  font-size: 18px;
}

.invoice-row-card.is-pending .invoice-status-line {
  background: #ffebee;
  color: #c62828;
}

.invoice-row-card.is-grace .invoice-status-line {
  background: #fff3e0;
  color: #ef6c00;
}

.invoice-row-card.is-complete .invoice-status-line,
.invoice-row-card.is-deleted .invoice-status-line {
  background: #e8f5e9;
  color: #2e7d32;
}

.invoice-row-card.is-deleted .invoice-status-line {
  background: #eceff1;
  color: #455a64;
}

.invoice-status-line :deep(.red-text),
.invoice-status-line :deep(.orange-text) {
  color: inherit !important;
}

.invoice-client {
  align-items: center;
  color: #263238;
  display: flex;
  flex-wrap: wrap;
  font-size: 0.98rem;
  font-weight: 700;
  gap: 8px;
  margin: 0;
}

.invoice-category {
  background: #f5f5f5;
  border: 1px solid #eeeeee;
  border-radius: 4px;
  color: #607d8b;
  font-size: 0.76rem;
  font-weight: 700;
  padding: 3px 7px;
}

.invoice-amount {
  background: transparent;
  border: 0;
  border-radius: 0;
  color: #263238;
  display: inline-flex;
  flex-wrap: wrap;
  font-size: 1.05rem;
  font-weight: 800;
  margin: 0;
  padding: 0;
}

.invoice-period {
  color: #607d8b;
  display: inline-flex;
  font-size: 0.85rem;
  font-weight: 600;
  gap: 5px;
  margin-left: 12px;
}

.invoice-row-card .col.s10 {
  align-items: center;
  display: grid;
  gap: 10px;
  grid-template-columns: minmax(170px, 0.9fr) minmax(280px, 1.45fr) minmax(300px, 1.15fr);
}

.invoice-action {
  align-items: center;
  background: #ffffff;
  border: 1px solid #eeeeee;
  border-radius: 50%;
  color: #607d8b;
  cursor: pointer;
  display: inline-flex;
  height: 38px;
  justify-content: center;
  padding: 0;
  width: 38px;
}

.invoice-action.is-normal {
  background: #fafafa;
}

.invoice-action.is-late {
  background: #ffffff;
  border-color: #efdad7;
  color: #8d3f3f;
}

.invoice-action.is-close {
  background: #eceff1;
  color: #263238;
}

.invoice-action i {
  font-size: 21px;
}

.invoice-action-label {
  display: none;
}

.invoice-row-card:hover .invoice-action {
  background: #eceff1;
  color: #263238;
}

.invoice-empty-state {
  align-items: center;
  background: #ffffff;
  border: 1px solid #eeeeee;
  border-top: 0;
  border-radius: 0 0 6px 6px;
  color: #607d8b;
  display: flex;
  flex-direction: column;
  padding: 38px 18px;
  text-align: center;
}

.invoice-empty-icon {
  align-items: center;
  background: #fafafa;
  border: 1px solid #eeeeee;
  border-radius: 6px;
  color: #607d8b;
  display: inline-flex;
  height: 42px;
  justify-content: center;
  margin-bottom: 12px;
  width: 42px;
}

.invoice-empty-icon i {
  font-size: 22px;
}

.invoice-empty-state h6 {
  color: #263238;
  font-weight: 700;
  margin: 0 0 6px;
}

.invoice-empty-state p {
  margin: 0;
}

.spacer10 {
  margin-top: 10px;
  margin-bottom: 10px;
}

.pointer {
  cursor: default;
}

.edit-item:hover .material-icons.grey-text,
.edit-item:hover .material-icons.grey-text::before {
  color: rgb(21, 21, 206) !important;
}

/* open */
.chip-more {
  cursor: pointer;
  background-color: #eaecee !important;
}

.chip-more:hover {
  color: white !important;
  background-color: black !important;
}

/* close */
.chip-less {
  cursor: pointer;
  color: white !important;
  background-color: black !important;
}

.chip-less:hover {
  color: black !important;
  background-color: #eaecee !important;
}

/* base class */
.tab-custom {
  display: inline-block;
  cursor: pointer;
  margin: 0 0 -1px;
  padding: 10px 15px;
  font-weight: 600;
  text-align: center;
  border: 1px solid transparent;
}

.tab-custom:hover {
  color: #000000 !important;
}

.tab-custom-active {
  display: inline-block;
  cursor: pointer;
  margin: 0 0 -1px;
  padding: 10px 15px;
  color: white;
  font-weight: 600;
  text-align: center;
  background-color: black;
}

/* display */
.tab-custom-section {
  padding: 20px 0 0;
  border-top: 1px solid #ddd;
}

@media only screen and (min-width: 601px) and (max-width: 992px) {
  .invoice-row-card > .row {
    align-items: center;
    display: grid;
    gap: 12px;
    grid-template-columns: 44px minmax(0, 1fr) 54px;
  }

  .invoice-row-card .col {
    padding: 0;
    width: 100% !important;
  }

  .invoice-row-card .col.s10 {
    grid-column: 2;
    grid-template-columns: minmax(150px, 0.9fr) minmax(220px, 1.45fr) minmax(210px, 1.15fr);
  }

  .invoice-row-card .col.s1:last-child {
    grid-column: 3;
  }

  .invoice-action {
    border-radius: 50%;
    box-shadow: 0 8px 18px -18px rgba(38, 50, 56, 0.72);
    height: 44px;
    width: 44px;
  }

  .invoice-action.is-normal,
  .invoice-action.is-late {
    background: #eceff1;
    border-color: #90a4ae;
    color: #263238;
  }

  .invoice-action.is-close {
    background: #cfd8dc;
    border-color: #78909c;
    color: #263238;
  }

  .invoice-action-label {
    display: none;
  }
}

@media only screen and (max-width: 600px) {
  .invoice-review-head {
    align-items: flex-start;
    flex-direction: column;
    gap: 14px;
    padding: 20px;
  }

  .invoice-review-head h5 {
    font-size: 1.35rem;
    line-height: 1.25;
  }

  .compliance-invoices {
    width: 100%;
  }

  .invoice-head-filter {
    flex-basis: auto;
    width: 100%;
  }

  .invoice-head-filter :deep(.row) {
    flex-direction: column;
    gap: 8px;
  }

  .invoice-head-filter :deep(.col) {
    border-left: 0 !important;
    width: 100% !important;
  }

  .invoice-head-filter :deep(.col + .col) {
    border-left: 0;
    border-top: 1px solid #eeeeee;
    padding-top: 8px !important;
  }

  .invoice-count {
    align-items: flex-start;
  }

  .invoice-row-card {
    padding: 16px;
  }

  .invoice-row-card > .row {
    align-items: stretch;
    display: grid;
    gap: 10px;
    grid-template-columns: 34px 1fr;
  }

  .invoice-row-card .col {
    margin-bottom: 10px;
    padding: 0;
    width: 100% !important;
  }

  .invoice-row-card .col.s10 {
    grid-column: 2;
    grid-template-columns: 1fr;
  }

  .invoice-row-card .col.s1:last-child {
    display: flex;
    grid-column: 2;
    justify-content: flex-end;
  }

  .invoice-action {
    border-radius: 50%;
    box-shadow: 0 8px 18px -18px rgba(38, 50, 56, 0.72);
    height: 44px;
    width: 44px;
  }

  .invoice-action.is-normal,
  .invoice-action.is-late {
    background: #eceff1;
    border-color: #90a4ae;
    color: #263238;
  }

  .invoice-action.is-close {
    background: #cfd8dc;
    border-color: #78909c;
    color: #263238;
  }

  .invoice-action-label {
    display: none;
  }
}
</style>
    
  margin-bottom: 0;
